/**
 * hitEffectVfx.ts
 * Pooled laser-hit VFX spawned at projectile–enemy collision points.
 *
 * AGENTS.md mandates pooling for "projectiles, particles, hit effects".
 * Every other entry on that list (projectiles, particles, asteroids,
 * boss missiles, boss projectiles, homing mines, explosions) already
 * lives in a pool — this module closes the last gap.
 *
 * Pool semantics mirror `explosionVfx.ts`:
 *  - {@link spawnHitEffect} acquires the first inactive slot. On a
 *    saturated pool the spawn is silently dropped — VFX must never
 *    block gameplay correctness.
 *  - {@link stepHitEffects} advances every active instance by `dt` and
 *    deactivates any whose elapsed time has passed the total burst
 *    duration.
 *  - {@link resetHitEffectVfx} flips every slot back to inactive.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD (retrofit).
 *
 * @module hitEffectVfx
 */

// DECISION: 6 frames at 16 fps = 0.375 s — matches LASER_HIT_CLIPS.BURST in
// `SpriteRenderer.ts`. Duplicated here (rather than imported) to keep the
// engine asset-agnostic; the renderer is the canonical source for sprite
// timing and the engine only needs a matching total duration.
/** Total burst duration in seconds. Must match LASER_HIT_CLIPS.BURST timing. */
export const HIT_EFFECT_DURATION_S = 6 / 16;

/**
 * Maximum simultaneously active laser-hit bursts. AGENTS.md caps VFX
 * pools at 50; 16 covers worst-case combat density (multiple shards
 * hitting a tight enemy cluster in one tick) with room to spare.
 */
export const HIT_EFFECT_POOL_SIZE = 16;

/**
 * One pooled hit-effect instance. Fields are public so the renderer
 * can read them without allocating a copy. Mutate only via the
 * functions in this module.
 */
export interface HitEffectInstance {
  /** Centre-X in world space (px). */
  x: number;
  /** Centre-Y in world space (px). */
  y: number;
  /** Seconds since `spawnHitEffect()`. Drives the renderer frame index. */
  elapsed: number;
  /** `true` between `spawn` and natural expiry; `false` while pooled. */
  active: boolean;
}

/**
 * Build a fresh, fully-inactive pool. Module-private factory; tests
 * reach for {@link resetHitEffectVfx} when they want a clean slate.
 *
 * @returns A pre-sized pool with every slot inactive.
 */
function createPool(): HitEffectInstance[] {
  const pool: HitEffectInstance[] = [];
  for (let i = 0; i < HIT_EFFECT_POOL_SIZE; i++) {
    pool.push({ x: 0, y: 0, elapsed: 0, active: false });
  }
  return pool;
}

/** Singleton pool. Module-scoped so renderer + engine share one source. */
const pool: HitEffectInstance[] = createPool();

/**
 * Acquire a hit-effect at `(x, y)`. Silently drops on a full pool — a
 * saturated pool is preferable to evicting an in-flight burst mid-render.
 *
 * @param x - World-space centre X for the burst.
 * @param y - World-space centre Y for the burst.
 * @returns The acquired instance, or `null` if the pool is saturated.
 */
export function spawnHitEffect(x: number, y: number): HitEffectInstance | null {
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    if (!inst.active) {
      inst.x = x;
      inst.y = y;
      inst.elapsed = 0;
      inst.active = true;
      return inst;
    }
  }
  return null;
}

/**
 * Advance every active hit-effect by one fixed timestep. Deactivates
 * any instance whose elapsed time has passed the total burst duration.
 *
 * @param dt - Fixed timestep in seconds.
 * @returns Nothing; pool slots are mutated in place.
 */
export function stepHitEffects(dt: number): void {
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    if (!inst.active) continue;
    inst.elapsed += dt;
    if (inst.elapsed >= HIT_EFFECT_DURATION_S) {
      inst.active = false;
    }
  }
}

/**
 * Read-only view of the pool. Callers must not mutate the returned
 * array or its instances — use {@link spawnHitEffect} /
 * {@link stepHitEffects} / {@link resetHitEffectVfx} instead.
 *
 * Returns the full pool (including inactive slots) so the renderer can
 * skip on `inst.active === false` without allocating a filtered copy
 * on every frame.
 *
 * @returns The shared pool array; treat as immutable.
 */
export function getHitEffectPool(): readonly HitEffectInstance[] {
  return pool;
}

/**
 * Reset every pool slot to the inactive defaults. Call on scene change
 * or death-respawn so a burst spawned in the old session doesn't carry
 * over into the next one's first render.
 *
 * @returns Nothing; pool slots are mutated in place.
 */
export function resetHitEffectVfx(): void {
  for (let i = 0; i < pool.length; i++) {
    const inst = pool[i];
    inst.x = 0;
    inst.y = 0;
    inst.elapsed = 0;
    inst.active = false;
  }
}
